import React, {useEffect, useState} from "react";
import {Params, useNavigate, useParams} from "react-router-dom";
import ComicPageView from "../../../../../components/comic-page/ComicPageView";
import ComicControls from "../../../../../components/comic-controls/ComicControls";
import ComicCommentary from "../../../../../components/comic-commentary/ComicCommentary";
import ContentBox from "../../../../../components/content-box/ContentBox";
import Modal from "../../../../../components/modal/Modal";
import ComicMetadata from "../../../../../model/ComicMetadata";
import ComicNavigationActions from "../../../../../model/ComicNavigationActions";
import PageActions from "../../../../base/PageActions";
import useBrowserNavigation from "../../../../../hooks/useBrowserNavigation";
import useComicKeyboardNavigation from "../../../../../hooks/useComicKeyboardNavigation";
import useFetchResult from "../../../../../hooks/UseFetchResult";
import _naming_service from "../../../../../services/NamingService";
import _http_service from "../../../../../services/HttpService";
import _image_service from "../../../../../services/ImageService";
import '../../../../styles/Common.scss';
import '../ComicPage.scss';
import './ComicDisplayPage.scss';

interface ComicDisplayPageProps {
}

interface ComicDisplayPageParams extends Params {
    comic: string,
    chapter?: string,
    page?: string
}

interface ComicDisplayPageResult {
    allPages: {[page: number] : ComicMetadata},
    maxPage: number
}

const ComicDisplayPage = (props: ComicDisplayPageProps) => {

    const params = useParams<ComicDisplayPageParams>();

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    const getFullComicTitle = (): string => {
        return `${_naming_service.capitalizeName(params.comic || '')} ${params.chapter}-${params.page}`;
    }

    const page: PageActions = new PageActions({
        title: `${getFullComicTitle()} - Chase the Fox`,
        description: `Page ${params.page}, Chapter ${params.chapter} of ${_naming_service.capitalizeName(params.comic || '')}, story and art by Chase the Fox!`,
        keywords: ["comic", "webcomic", params.comic || '',
            _naming_service.capitalizeName(params.comic || '').toLowerCase()],
        imgSrc: `chase/comic/talechasers/tale_chasers_logo.png`,
        url: `comics/${params.comic}/${params.chapter}/${params.page}`
    });
    const [isLoading, setLoading] = useState<boolean>(true);

    const navigateTo = useNavigate();

    const [getResult, hasFetchedResult] = useFetchResult<ComicDisplayPageResult>(undefined, async () => {
        if (!params.comic || params.comic.trim().length === 0
                || !params.chapter || params.chapter.trim().length === 0 || +params.chapter < 0) {
            navigateTo("/error", {replace: true});
            throw new Error("Invalid comic parameters!");
        }

        return _http_service.getComicChapterPages(params.comic, +params.chapter)
            .then(response => {
                if (!response) {
                    throw new Error("Could not fetch comic page!");
                }

                return {
                    allPages: response,
                    maxPage: +Object.keys(response).reduce((a, b) => ''+Math.max(+a, +b))
                };
            });
    }, [params.comic, params.chapter]);

    const [getHasNextChapter, hasCheckedNextChapter] = useFetchResult<boolean>(
        false,
        async () => {
            const comic = params.comic || '';
            const nextChapter = (params.chapter ? +params.chapter : 0) + 1;
            if (!comic || nextChapter <= 0) return false;
            return _http_service.getComicChapterPages(comic, nextChapter)
                .then(response => !!response && Object.keys(response).length > 0);
        },
        [params.comic, params.chapter],
        () => false
    );

    useBrowserNavigation(() => {
        setLoading(true);
    }, false);

    const goToPage = (page: number): void => {
        setLoading(true);
        navigateTo(`../${page}`);
    }

    const goToChapter = (chapter: number): void => {
        setLoading(true);
        navigateTo(`../../${chapter}/1`);
    }

    const currentPage = params.page ? +params.page : 0;
    const maxPage = getResult() ? getResult().maxPage : 0;
    const currentChapter = params.chapter ? +params.chapter : 0;
    const hasNextChapter = hasCheckedNextChapter && getHasNextChapter();

    const comicNav: ComicNavigationActions = {
        goToFirst: currentPage > 1
            ? () => goToPage(1)
            : currentChapter > 1
                ? () => goToChapter(currentChapter - 1)
                : undefined,
        goToPrevious: currentPage > 1 ? () => goToPage(currentPage - 1) : undefined,
        goToNext: currentPage < maxPage ? () => goToPage(currentPage + 1) : undefined,
        goToLatest: currentPage < maxPage
            ? () => goToPage(maxPage)
            : hasNextChapter
                ? () => goToChapter(currentChapter + 1)
                : undefined
    };

    useComicKeyboardNavigation({
        actions: comicNav,
        enabled: !modalIsOpen
    });

    const getCurrentPageMetadata = (): ComicMetadata => {
        let page = params.page ? +params.page : -1;
        if (getResult() && page >= 0 && Object.keys(getResult().allPages).includes(''+page)) {
            return getResult()!.allPages[page];
        }

        return {commentary: "", imageId: "", page: page};
    }

    useEffect(() => {
        if (!hasFetchedResult || !getResult()) return;

        const currentPage = params.page ? +params.page : -1;
        const allPages = getResult().allPages;

        [currentPage - 1, currentPage + 1].forEach(adjPage => {
            if (adjPage >= 0 && Object.keys(allPages).includes('' + adjPage)) {
                const img = new Image();
                img.src = _image_service.getCdnImageSrc(
                    `comics/talechasers/${params.chapter}/${allPages[adjPage].imageId}.png`);
            }
        });
    }, [params.page, hasFetchedResult]);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)}>
                <div className="comic-modal-view">
                    <img src={_image_service.getCdnImageSrc(
                            `comics/talechasers/${params.chapter}/${getCurrentPageMetadata().imageId}.png`,
                            'desktop')}
                         alt={getFullComicTitle()}
                         title={getFullComicTitle()}/>
                </div>
            </Modal>
            <ContentBox key={`comic-${params.comic}-${params.chapter}`}>
                <img className="comic-header"
                     src={_image_service.getCdnImageSrc(`chase/comic/talechasers/tale_chasers_logo.png`)}
                     alt="Tale Chasers"
                     title="Art and story by Chase the Fox"/>
                <ComicControls actions={comicNav}
                               canGoToPreviousChapter={() => currentPage === 1 && (!!params.chapter && +params.chapter > 1)}
                               canGoToNextChapter={() => currentPage === maxPage && hasNextChapter}/>
                {hasFetchedResult
                    ? <ComicPageView webImageSrc={_image_service.getCdnImageSrc(
                                   `comics/talechasers/${params.chapter}/${getCurrentPageMetadata().imageId}.png`)}
                                     loading={isLoading}
                                     onLoad={() => setLoading(false)}
                                     onClick={() => setModalIsOpen(true)}/>
                    : <></>}
                <ComicCommentary comment={getCurrentPageMetadata().commentary}/>
                <ComicControls actions={comicNav}
                               canGoToPreviousChapter={() => currentPage === 1 && (!!params.chapter && +params.chapter > 1)}
                               canGoToNextChapter={() => currentPage === maxPage && hasNextChapter}/>
            </ContentBox>
        </>
    );
}

export default ComicDisplayPage;